// 같은 영상으로 전체 화면과 미니 모드를 전환하고, 페이지 이동 시 재생 위치를 복원합니다.
(() => {
  if (customElements.get('cj-shoppable-video')) return;
  const script = document.getElementById('cj-shoppable-video-script');
  const canPersist = script?.dataset.persist === 'true';
  const storageKey = `cj:shoppable-video:${script?.dataset.theme || 'preview'}`;
  const ttl = 2 * 60 * 60 * 1000;
  const clearSaved = () => { try { sessionStorage.removeItem(storageKey); } catch (_) {} };
  const readSaved = () => {
    if (!canPersist) return null;
    try {
      const state = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (state?.version === 1 && /^[\w-]+$/.test(state.sectionId) &&
          (state.playerId == null || /^[\w-]+$/.test(state.playerId)) &&
          typeof state.videoId === 'string' && Number.isFinite(state.time) && state.time >= 0 &&
          Number.isFinite(state.savedAt) && Date.now() - state.savedAt >= 0 && Date.now() - state.savedAt < ttl) return state;
    } catch (_) {}
    clearSaved();
    return null;
  };

  const isSavedPlayer = (player, state) => state && player.dataset.sectionId === state.sectionId &&
    (player.dataset.playerId || '') === (state.playerId || '');

  class ShoppableVideo extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-open]');
      if (!this.dialog || (!this.opener && !this.hasAttribute('data-external-triggers'))) return;
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.mode = 'closed';
      if (this.opener) {
        this.opener.disabled = false;
        this.opener.addEventListener('click', () => this.open(), options);
      }
      document.querySelectorAll('[data-cj-video-target]').forEach(trigger => {
        if (trigger.dataset.cjVideoTarget === this.id) trigger.disabled = false;
      });
      this.querySelector('[data-close]').addEventListener('click', () => this.close(), options);
      this.querySelector('[data-size]').addEventListener('click', () => this.setMode(this.mode === 'mini' ? 'full' : 'mini'), options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open && this.mode !== 'closed') this.close();
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.mode === 'mini') { event.preventDefault(); this.close(); }
      }, options);
      this.querySelector('[data-play]').addEventListener('click', () => {
        if (this.video?.paused) this.play();
        else { this.wantsPlayback = false; this.video?.pause(); this.persist(); }
      }, options);
      this.querySelector('[data-mute]').addEventListener('click', () => {
        if (this.video) { this.video.muted = !this.video.muted; this.updateControls(); this.persist(); }
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && this.mode === 'full') this.video?.pause();
        if (document.hidden && this.mode === 'mini') this.persist();
      }, options);
      window.addEventListener('pagehide', () => { this.persist(); this.leaving = true; this.unlockScroll(); }, options);
      window.addEventListener('pageshow', event => {
        this.leaving = false;
        if (!event.persisted) return;
        const saved = readSaved();
        if (this.mode === 'mini' && isSavedPlayer(this, saved)) this.restore(saved);
        else if (this.mode !== 'closed') this.close();
      }, options);
      window.addEventListener('resize', () => this.clampPosition(), options);
      this.bindDrag(options);
      this.bindProducts(options);
      const saved = this.resumeState || readSaved();
      if (isSavedPlayer(this, saved) && saved.videoId === this.dataset.videoId) this.restore(saved);
      else if (isSavedPlayer(this, saved)) clearSaved();
    }

    ensureVideo() {
      if (this.video) return;
      this.querySelector('[data-video-mount]').append(this.querySelector('[data-video-template]').content.cloneNode(true));
      this.video = this.querySelector('video');
      this.video.controls = false;
      this.video.muted = true;
      const options = { signal: this.abort.signal };
      ['play', 'pause', 'volumechange', 'ended'].forEach(event => this.video.addEventListener(event, () => { this.updateControls(); this.persist(); }, options));
      this.video.addEventListener('ended', () => {
        if (!this.video.loop) { this.wantsPlayback = false; this.persist(); }
      }, options);
      this.video.addEventListener('timeupdate', () => {
        if (Date.now() - (this.lastSave || 0) > 1000) this.persist();
      }, options);
      this.video.addEventListener('error', () => { this.querySelector('[data-error]').hidden = false; }, options);
      this.updateControls();
    }

    open(trigger) {
      if (trigger instanceof HTMLElement) this.opener = trigger;
      document.querySelectorAll('cj-shoppable-video').forEach(player => { if (player !== this && player.dialog?.open) player.close(false); });
      this.ensureVideo();
      this.setMode('full');
      this.play();
    }

    setMode(mode, focus = true) {
      if (this.mode === mode) return;
      const wasPlaying = this.video && !this.video.paused;
      this.mode = mode;
      this.dialog.close();
      this.dialog.classList.toggle('is-mini', mode === 'mini');
      const toggle = this.querySelector('[data-size]');
      toggle.setAttribute('aria-label', mode === 'mini' ? toggle.dataset.expand : toggle.dataset.minimize);
      toggle.title = toggle.getAttribute('aria-label');
      if (mode === 'mini') {
        this.unlockScroll();
        this.dialog.show();
        this.clampPosition();
      } else {
        clearSaved();
        this.resetPosition();
        if (!this.scrollLocked) {
          this.previousOverflow = document.documentElement.style.overflow;
          this.scrollLocked = true;
          document.documentElement.style.overflow = 'hidden';
        }
        this.dialog.showModal();
      }
      if (wasPlaying) this.play();
      if (focus) toggle.focus({ preventScroll: true });
      this.notifyMode();
      this.persist();
    }

    restore(state) {
      this.restoring = true;
      this.wantsPlayback = state.playing === true;
      this.ensureVideo();
      this.setMode('mini', false);
      const resume = () => {
        if (!this.isConnected || this.mode !== 'mini') { this.restoring = false; return; }
        const duration = this.video.duration;
        this.video.currentTime = Number.isFinite(duration) && duration > 0 ? Math.min(state.time, Math.max(0, duration - .1)) : state.time;
        this.video.muted = state.muted !== false;
        this.restoring = false;
        if (state.playing) this.play(); else this.video.pause();
        this.updateControls();
      };
      if (this.video.readyState >= 1) resume();
      else {
        this.video.addEventListener('loadedmetadata', resume, { once: true, signal: this.abort.signal });
        this.video.preload = 'metadata';
        this.video.load();
      }
    }

    play() {
      if (!this.video) return;
      this.querySelector('[data-error]').hidden = true;
      if (this.video.error) this.video.load();
      this.wantsPlayback = true;
      this.video.play().catch(() => {
        if (!this.leaving && !document.hidden) this.wantsPlayback = false;
        this.updateControls();
        this.persist();
      });
    }

    updateControls() {
      if (!this.video) return;
      const play = this.querySelector('[data-play]');
      const mute = this.querySelector('[data-mute]');
      play.textContent = this.video.paused ? play.dataset.paused : play.dataset.playing;
      mute.textContent = this.video.muted ? mute.dataset.muted : mute.dataset.audible;
    }

    persist() {
      if (!canPersist || this.mode !== 'mini' || !this.video || this.restoring || this.leaving) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ version: 1, sectionId: this.dataset.sectionId,
          playerId: this.dataset.playerId || '', videoId: this.dataset.videoId, time: this.video.currentTime || 0, playing: this.wantsPlayback === true,
          muted: this.video.muted, savedAt: Date.now() }));
        this.lastSave = Date.now();
      } catch (_) {}
    }

    close(restoreFocus = true) {
      this.mode = 'closed';
      this.wantsPlayback = false;
      clearSaved();
      this.video?.pause();
      this.dialog?.close();
      this.dialog?.classList.remove('is-mini');
      this.resetPosition();
      this.unlockScroll();
      this.notifyMode();
      if (restoreFocus && this.isConnected && !this.hasAttribute('data-restored')) this.opener?.focus({ preventScroll: true });
      if (this.hasAttribute('data-restored')) this.remove();
    }

    notifyMode() {
      const banner = this.dataset.bannerId && document.getElementById(this.dataset.bannerId);
      banner?.dispatchEvent(new CustomEvent('cj:video-mode', { detail: { mode: this.mode } }));
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      document.documentElement.style.overflow = this.previousOverflow;
      this.scrollLocked = false;
    }

    resetPosition() {
      this.position = null;
      this.dialog?.style.removeProperty('left');
      this.dialog?.style.removeProperty('top');
      this.dialog?.style.removeProperty('right');
      this.dialog?.style.removeProperty('bottom');
    }

    clampPosition() {
      if (this.mode !== 'mini' || !this.position) return;
      const rect = this.dialog.getBoundingClientRect();
      this.position.x = Math.max(8, Math.min(this.position.x, innerWidth - rect.width - 8));
      this.position.y = Math.max(8, Math.min(this.position.y, innerHeight - rect.height - 8));
      Object.assign(this.dialog.style, { left: `${this.position.x}px`, top: `${this.position.y}px`, right: 'auto', bottom: 'auto' });
    }

    bindDrag(options) {
      const handle = this.querySelector('[data-drag-handle]');
      handle.addEventListener('pointerdown', event => {
        if (this.mode !== 'mini' || event.button !== 0) return;
        const rect = this.dialog.getBoundingClientRect();
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top };
        handle.setPointerCapture(event.pointerId);
      }, options);
      handle.addEventListener('pointermove', event => {
        if (this.drag?.id !== event.pointerId) return;
        this.position = { x: this.drag.left + event.clientX - this.drag.x, y: this.drag.top + event.clientY - this.drag.y };
        this.clampPosition();
      }, options);
      const stop = () => { this.drag = null; };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(event => handle.addEventListener(event, stop, options));
    }

    bindProducts(options) {
      const list = this.querySelector('[data-products]');
      if (!list) return;
      let drag;
      let moved = false;
      list.addEventListener('dragstart', event => event.preventDefault(), options);
      list.addEventListener('pointerdown', event => {
        moved = false;
        if (event.pointerType !== 'mouse' || event.button !== 0 || list.scrollWidth <= list.clientWidth) return;
        drag = { id: event.pointerId, x: event.clientX, left: list.scrollLeft };
      }, options);
      list.addEventListener('pointermove', event => {
        if (drag?.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        if (!moved && Math.abs(dx) < 8) return;
        moved = true;
        list.setPointerCapture(event.pointerId);
        list.classList.add('is-dragging');
        list.scrollLeft = drag.left - dx;
        event.preventDefault();
      }, options);
      const stop = event => {
        if (drag?.id !== event.pointerId) return;
        drag = null;
        if (list.hasPointerCapture(event.pointerId)) list.releasePointerCapture(event.pointerId);
        list.classList.remove('is-dragging');
      };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(event => list.addEventListener(event, stop, options));
      list.addEventListener('click', event => {
        if (!moved) return;
        event.preventDefault(); event.stopImmediatePropagation(); moved = false;
      }, { ...options, capture: true });
      list.addEventListener('keydown', event => {
        if (event.target !== list || !['ArrowLeft', 'ArrowRight'].includes(event.key) || list.scrollWidth <= list.clientWidth) return;
        event.preventDefault();
        list.scrollBy({ left: list.clientWidth * (event.key === 'ArrowRight' ? .85 : -.85), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, options);
    }

    disconnectedCallback() {
      this.mode = 'closed';
      this.video?.pause();
      this.dialog?.close();
      this.unlockScroll();
      this.abort?.abort();
    }
  }
  customElements.define('cj-shoppable-video', ShoppableVideo);

  const restoreOnPage = async () => {
    const saved = readSaved();
    if (!saved || [...document.querySelectorAll('cj-shoppable-video')].some(el => isSavedPlayer(el, saved))) return;
    try {
      const url = new URL(script.dataset.root || '/', location.origin);
      if (url.origin !== location.origin) return;
      url.searchParams.set('sections', saved.sectionId);
      const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(8000) });
      if (!response.ok) return;
      const sections = await response.json();
      const html = sections[saved.sectionId];
      if (typeof html !== 'string') { clearSaved(); return; }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = [...parsed.querySelectorAll('cj-shoppable-video')].find(el => isSavedPlayer(el, saved));
      if (!source || source.dataset.videoId !== saved.videoId) { clearSaved(); return; }
      if (!isSavedPlayer(source, readSaved()) || [...document.querySelectorAll('cj-shoppable-video')].some(el => isSavedPlayer(el, saved))) return;
      source.querySelectorAll('script').forEach(el => el.remove());
      const player = document.importNode(source, true);
      player.setAttribute('data-restored', '');
      player.resumeState = saved;
      document.body.append(player);
    } catch (_) {}
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreOnPage, { once: true });
  else restoreOnPage();
})();
